import { EventEmitter, Injectable, Inject } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  SilentRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { filter, BehaviorSubject, Subject, takeUntil, tap } from 'rxjs';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  eventDetailToken = new EventEmitter<AuthenticationResult | undefined>();
  private readonly _destroying$ = new Subject<void>();
  detailToken: any;
  public intervalRefresh = 60000;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(
    private msalService: MsalService,
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly msalBroadcastService: MsalBroadcastService
  ) { }

  msalInit = (success: () => void) => {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount(success);
      });
  };

  checkAndSetActiveAccount = (success: () => void) => {
    this.ensureActiveAccountIsSet();

    const token = this.getCurrentToken();
    if (token) {
      this.detailToken = {
        accessToken: token,
        expiresOn: dayjs(localStorage.getItem('expiresOn')!).toDate(),
      };

      this.isAuthenticatedSubject.next(true);
      this.eventDetailToken.emit(this.detailToken);
      success();
      return;
    }

    this.requestAndStoreToken(success);
  };

  deleteToken() {
    localStorage.removeItem('token');
    localStorage.removeItem('expiresOn');
  }

  onAcquireTokenSilentError = (
    authRequest?: any,
    tapPipe: any = tap(() => { })
  ) => {
    this.deleteToken();

    let loginRedirect = this.msalService.loginRedirect();
    if (authRequest) {
      loginRedirect = this.msalService.loginRedirect({
        ...authRequest,
      } as RedirectRequest);
    }

    loginRedirect.pipe(tapPipe).subscribe();
  };

  refreshToken() {
    if (this.detailToken) {
      const now = dayjs();
      const expirationTime = dayjs(this.detailToken.expiresOn);
      if (
        expirationTime.isBefore(now.add(this.intervalRefresh, 'millisecond'))
      ) {
        this.requestAndStoreToken();
      }
    }
  }

  private requestAndStoreToken(success?: () => void) {
    this.msalService.instance
      .acquireTokenSilent({
        ...this.msalGuardConfig.authRequest,
      } as SilentRequest)
      .then((result: AuthenticationResult) => {
        if (result) {
          this.detailToken = result;
          localStorage.setItem('token', result.accessToken);

          if (result.expiresOn) {
            localStorage.setItem('expiresOn', result.expiresOn.toISOString());
          }

          this.isAuthenticatedSubject.next(true);
          this.eventDetailToken.emit(result);

          if (success) success();
        } else {
          this.onAcquireTokenSilentError(this.msalGuardConfig.authRequest);
        }
      })
      .catch((error) => {
        console.error('Error renewing token:', error);
        this.onAcquireTokenSilentError(this.msalGuardConfig.authRequest);
      });
  }

  startTokenAutoRefresh() {
    setInterval(() => {
      this.refreshToken();
    }, this.intervalRefresh);
  }

  ensureActiveAccountIsSet(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const allAccounts = this.msalService.instance.getAllAccounts();

    if (!activeAccount && allAccounts.length > 0) {
      this.msalService.instance.setActiveAccount(allAccounts[0]);
    }
  }

  getCurrentToken(): string | null {
    const token = localStorage.getItem('token');
    const expiresOn = localStorage.getItem('expiresOn');

    if (token && expiresOn) {
      const expirationTime = dayjs(expiresOn);
      const now = dayjs();

      if (expirationTime.isAfter(now)) {
        return token;
      } else {
        this.deleteToken();
      }
    }
    return null;
  }

  refreshTokenIfNeeded(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }
    this.isRefreshing = true;
    this.refreshPromise = this.msalService.instance
      .acquireTokenSilent({
        ...this.msalGuardConfig.authRequest,
      } as SilentRequest)
      .then((result: AuthenticationResult) => {
        this.detailToken = result;
        localStorage.setItem('token', result.accessToken);
        if (result.expiresOn) {
          localStorage.setItem('expiresOn', result.expiresOn.toISOString());
        }
        this.isAuthenticatedSubject.next(true);
        this.eventDetailToken.emit(result);
        return result.accessToken;
      })
      .catch((error) => {
        console.error('[AuthService] Silent token refresh failed:', error);
        this.onAcquireTokenSilentError(this.msalGuardConfig.authRequest);
        return null;
      })
      .finally(() => {
        this.isRefreshing = false;
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }
}
